import { useEffect, useState } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';

import type { PagedConversations } from '@/features/conversations/conversations.api';
import {
  CONVERSATIONS_ROOT,
  type ConversationsListKeyParams,
} from '@/features/conversations/keys';

/** Shape stored in react-query for the conversations infinite list. */
type InfiniteConversations = { pages: PagedConversations[]; pageParams: unknown[] };

/**
 * True when `key` is a conversations-LIST key whose params scope it to
 * `accountId` (`null` = the aggregate "所有账号" lists) — exactly the key
 * space `useAccountUnreadSum` sums over. Shared by the render-time
 * `getQueriesData` read and the cache-subscription filter below so the two
 * can never drift apart.
 */
function isScopedConversationsListKey(key: QueryKey, accountId: string | null): boolean {
  if (!Array.isArray(key) || key.length < 3) return false;
  if (key[0] !== CONVERSATIONS_ROOT || key[1] !== 'list') return false;
  const params = key[2] as ConversationsListKeyParams | undefined;
  return params?.accountId === accountId;
}

/**
 * Sum `unread_count` across all cached conversations-list queries scoped to
 * `accountId` — `null` matches the aggregate "所有账号" list instead of
 * a single account's lists. The sum is DEDUPLICATED by conversation id (see
 * the body comment — the filter tabs keep overlapping copies of the same
 * row cached, so a raw sum would double/triple count).
 *
 * Re-render trigger strategy: we subscribe to QueryCache notifications with a
 * counter that increments on relevant cache updates, then derive the sum
 * inside the render. This is the simplest approach that reacts to both REST
 * refetches AND the WS projection patches (`setQueryData`) from M5-T6 — both
 * dispatch an `updated` cache notification. Using `useQuery` with `queryFn`
 * would introduce a superfluous network-visible key; `getQueriesData` inside
 * the counter re-render reads the live cache without any extra fetches.
 *
 * The subscription is deliberately narrow, in two dimensions (same rationale
 * as `ChatPanelInner`'s lock-tick subscription — see ChatPanel.tsx, where the
 * unscoped version of this pattern closed a self-sustaining render loop):
 *
 *   - EVENT TYPE — only `updated` / `removed` can change the data the sum is
 *     derived from, and neither ever fires during a React render. Crucially
 *     this excludes `added`: react-query creates a query synchronously INSIDE
 *     the render of whichever component first mounts a new key
 *     (`useState(() => new QueryObserver(...))` → `QueryCache.build` →
 *     `notify({ type: 'added' })`), so an `added`-driven `setTick` would
 *     update this row mid-render of that component — React's "Cannot update
 *     a component while rendering a different component" dev warning. An
 *     account switch does exactly this on every remount of
 *     `ConversationList` (fresh list key) and its rows (fresh `enabled:
 *     false` lock-probe keys). A just-added query carries no data (nothing
 *     in this app uses `initialData`, and `setQueryData` on a missing key
 *     fires `added` immediately followed by `updated`), so skipping `added`
 *     loses no repaint. It also excludes `observerOptionsUpdated`, which
 *     every mounted useQuery re-emits after each of its own re-renders.
 *   - QUERY KEY — only the conversations-list keys whose `accountId` param
 *     matches this row feed the sum; everything else (the accounts poll,
 *     detail/lock churn, observer add/remove noise) is irrelevant — the old
 *     unscoped subscription re-rendered every sidebar row on every one of
 *     those events.
 *
 * Shared by `AccountRow` (per-account badge, called with a real account id)
 * and `AllAccountsRow` (the pinned "所有账号" badge, called with `null`) — same
 * "cached queries only" approximation semantics either way: nothing is
 * fetched on this hook's behalf, it only sums what's already in the cache.
 */
export function useAccountUnreadSum(accountId: string | null): number {
  const queryClient = useQueryClient();
  // Counter forces re-render on relevant cache mutations; the sum is derived
  // each render.
  const [, setTick] = useState(0);

  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' && event.type !== 'removed') return;
      if (!isScopedConversationsListKey(event.query.queryKey, accountId)) return;
      setTick((t) => t + 1);
    });
    return unsubscribe;
  }, [queryClient, accountId]);

  const matchingQueries = queryClient.getQueriesData<InfiniteConversations>({
    predicate: (query) => isScopedConversationsListKey(query.queryKey, accountId),
  });

  // The redesigned filter tabs make cache overlap SYSTEMATIC, not
  // incidental: the "All" list and the `view=unread_pin` / `view=cuci`
  // lists for the same `accountId` scope stay cached side by side (~5min
  // gcTime after a tab switch), `view=unread_pin` is by construction a
  // SUBSET of "All", and the WS projector patches the SAME conversation
  // into every cached copy. Summing raw rows across caches would therefore
  // count each unread conversation once PER CACHED TAB (~2x/3x badges).
  // Dedup by conversation id — last write wins, which is safe precisely
  // because the projector patches every copy identically.
  const unreadById = new Map<string, number>();
  for (const [, data] of matchingQueries) {
    if (!data) continue;
    for (const page of data.pages) {
      for (const conv of page.items) {
        unreadById.set(conv.id, conv.unread_count);
      }
    }
  }
  let sum = 0;
  for (const count of unreadById.values()) {
    sum += count;
  }
  return sum;
}
